import Combobox from "@/components/Combobox";
import InputError from "@/components/InputError";
import SkeletonRow from "@/components/SkeletonRow";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { web } from "@/config/System";
import {
  listAkreditasi,
  listHubunganKeluarga, // Ensure this exists in your constants
  listKebutuhanKhusus,
  listLokasiKuliah,
  listSize,
} from "@/constants";
import { useDebounce } from "@/hooks/useDebounce";
import useFilePreview from "@/hooks/useFilePreview";
import useSessionStorage from "@/hooks/useSessionStorage";
import { useUser } from "@/hooks/useUser";
import {
  getBiodata,
  getListAgama,
  getListAlatTransportasi,
  getListDataPilihanProdi,
  getListJenisTinggal,
  getListNegara,
  getListPerguruanTinggi,
  getListPerguruanTinggiProdi,
  getListWilayah,
} from "@/services/getData";
import {
  Agama,
  AlatTransportasi,
  BiodataSate,
  DataPendaftaran,
  DataSiswa,
  HubunganKeluargaEnum, // Ensure this exists in your types
  JenisTinggal,
  LokasiKuliah,
  Negara,
  Option,
  PerguruanTinggi,
  PerguruanTinggiProdi,
  Size,
  Wilayah,
} from "@/types";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertCircle,
  Briefcase,
  Camera,
  Contact2,
  GraduationCap,
  MapPin,
  Ruler,
  User,
} from "lucide-react";
import { SetStateAction, useEffect, useRef, useState } from "react";
import { FaPlus } from "react-icons/fa6";
import { useSearchParams } from "react-router-dom";

// --- REUSABLE WRAPPER COMPONENT ---
const FormSection = ({
  icon,
  title,
  children,
  className = "",
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 ${className}`}
    >
      <h3 className="mb-4 flex items-center gap-2 border-b pb-2 font-bold text-slate-800 dark:text-slate-200">
        {icon} {title}
      </h3>
      {children}
    </div>
  );
};

export default function Biodata({
  form,
  setForm,
  error,
  file,
  setFile,
}: {
  form: BiodataSate;
  setForm: React.Dispatch<SetStateAction<BiodataSate>>;
  error: AxiosError<{
    message?: string;
    errors?: Record<string, string[]>;
  }> | null;
  file: File | null;
  setFile: React.Dispatch<SetStateAction<File | null>>;
}) {
  const { user, isPending: isPendingUser } = useUser();
  const preview = useFilePreview(file);
  const [searchParams] = useSearchParams();
  const jenjangParams = searchParams.get("jenjang");

  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const uploadRef = useRef<HTMLInputElement | null>(null);
  const [openAsalUniversitas, setOpenAsalUniversitas] = useState(false);
  const [openAsalProdi, setOpenAsalProdi] = useState(false);
  const [openProvinsi, setOpenProvinsi] = useState(false);
  const [openKabupaten, setOpenKabupaten] = useState(false);
  const [openKecamatan, setOpenKecamatan] = useState(false);
  const [openNegara, setOpenNegara] = useState(false);
  const [openAlatTransportasi, setOpenAlatTransportasi] = useState(false);
  const [idFeederProvinsi, setIdFeederProvinsi] = useState("");
  const [idFeederKabupaten, setIdFeederKabupaten] = useState("");
  const [searchQueryProvinsi, setSearchQueryProvinsi] = useState("");
  const [searchQueryKabupaten, setSearchQueryKabupaten] = useState("");
  const [searchQueryKecamatan, setSearchQueryKecamatan] = useState("");
  const [searchQueryUniversitas, setSearchQueryUniversitas] = useState("");

  const debouncedQueryUniversitas = useDebounce(searchQueryUniversitas, 1000);

  // --- QUERIES ---
  const { data: dataProvinsi, isPending: isPendingProvinsi } = useQuery({
    queryKey: ["listProvinsi", form.provinsi, searchQueryProvinsi],
    queryFn: () =>
      getListWilayah(
        10,
        "1",
        undefined,
        searchQueryProvinsi ? undefined : form.provinsi,
        searchQueryProvinsi,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
        id_feeder: `${wilayah?.id_feeder}`,
      })),
  });

  const {
    data: dataKabupaten,
    refetch: refetchKabupaten,
    isPending: isPendingKabupaten,
  } = useQuery({
    queryKey: [
      "listKabupaten",
      idFeederProvinsi,
      form.kabupaten,
      searchQueryKabupaten,
    ],
    queryFn: () =>
      getListWilayah(
        10,
        undefined,
        idFeederProvinsi,
        searchQueryKabupaten ? undefined : form.kabupaten,
        searchQueryKabupaten,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
        id_feeder: `${wilayah.id_feeder}`,
      })),
    enabled: !!idFeederProvinsi,
  });

  const {
    data: dataKecamatan,
    refetch: refetchKecamatan,
    isPending: isPendingKecamatan,
  } = useQuery({
    queryKey: [
      "listKecamatan",
      idFeederKabupaten,
      searchQueryKecamatan ? undefined : form.kecamatan,
      searchQueryKecamatan,
    ],
    queryFn: () =>
      getListWilayah(
        10,
        undefined,
        idFeederKabupaten,
        form.kecamatan,
        searchQueryKecamatan,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
      })),
    enabled: !!idFeederKabupaten,
  });

  const { data: dataNegara } = useQuery({
    queryKey: ["listNegara"],
    queryFn: () => getListNegara(),
    select: (data) =>
      data?.map((negara: Negara) => ({
        label: negara.nama_negara,
        value: `${negara.id_ref_negara}`,
      })),
  });

  const { data: dataAgama } = useQuery({
    queryKey: ["listAgama"],
    queryFn: () => getListAgama(),
  });

  const { data: dataJenisTinggal } = useQuery({
    queryKey: ["listJenisTinggal"],
    queryFn: () => getListJenisTinggal(),
  });
  const selectedJenisTinggal =
    dataJenisTinggal &&
    dataJenisTinggal?.find((item: JenisTinggal) => {
      return item.id_ref_jenis_tinggal == form.id_ref_jenis_tinggal;
    });

  const { data: dataAlatTransportasi } = useQuery({
    queryKey: ["listAlatTransportasi"],
    queryFn: () => getListAlatTransportasi(),
    select: (data) =>
      data?.map((alatTransportasi: AlatTransportasi) => ({
        label: alatTransportasi.nama_alat_transportasi,
        value: `${alatTransportasi.id_ref_alat_transportasi}`,
      })),
  });

  const { data: dataSiswa, isPending: isPendingDataSiswa } =
    useQuery<DataSiswa>({
      queryKey: ["dataSiswa", user?.id, sessionPeriode?.id_sys_periode],
      queryFn: () => getBiodata(sessionPeriode?.id_sys_periode || ""),
      enabled: !!user && !!sessionPeriode?.id_sys_periode,
    });
  const dataPendaftaran: DataPendaftaran | undefined =
    dataSiswa?.data_pendaftaran?.[0] || undefined;

  const { data: dataPilihanProdi } = useQuery({
    queryKey: ["listDataPilihanProdi"],
    queryFn: () => getListDataPilihanProdi(sessionPeriode?.id_sys_periode),
  });

  const { data: optionsPt, isPending: isPendingOptionsPt } = useQuery({
    queryKey: ["listPerguruanTinggi", debouncedQueryUniversitas, form.asal_pt],
    queryFn: () => getListPerguruanTinggi("1", "1", `${web.univAcronym}`),
    select: (data) => {
      if (!data) return [];
      return data?.map((item: PerguruanTinggi) => ({
        label: item.nama_perguruan_tinggi,
        value: `${item.id_feeder}`,
        list_prodi: item.pt_prodi,
      }));
    },
  });

  const { data: dataAsalProdi } = useQuery({
    queryKey: ["listAsalProdi", form.asal_pt],
    queryFn: () =>
      getListPerguruanTinggiProdi("1", "1000", form.asal_pt || "", false),
    select: (data) => {
      return data?.map((item: PerguruanTinggiProdi) => ({
        label: `${item.nama_program_studi} (${item.nama_jenjang_pendidikan})`,
        value: `${item.id_feeder}`,
        nama_jenjang_pendidikan: item.nama_jenjang_pendidikan,
      }));
    },
    enabled: !!form.asal_pt,
  });

  const optionsAsalProdi = dataAsalProdi
    ? dataAsalProdi?.filter((item: Option) => {
        return (
          item.nama_jenjang_pendidikan === "S1" ||
          item.nama_jenjang_pendidikan === "D4"
        );
      })
    : [];

  // --- EFFECTS ---
  useEffect(() => {
    if (form.provinsi) refetchKabupaten();
  }, [form.provinsi, refetchKabupaten]);

  useEffect(() => {
    if (form.kabupaten) refetchKecamatan();
  }, [form.kabupaten, refetchKecamatan]);

  useEffect(() => {
    if (!dataSiswa || !sessionPeriode?.id_sys_periode) return;

    setForm((prev) => ({
      ...prev,
      id_sys_periode: sessionPeriode?.id_sys_periode || "",
      ...dataSiswa,
      pilihan_lokasi_kuliah:
        (dataSiswa.data_pendaftaran?.[0]?.data_pilihan_prodi?.[0]?.pilihan_lokasi_kuliah?.toLowerCase() as LokasiKuliah) ??
        "",
      asal_pt: dataPendaftaran?.asal_pt,
      akreditasi_pt_asal: dataPendaftaran?.akreditasi_pt_asal,
      asal_prodi: dataPendaftaran?.asal_prodi,
      akreditasi_prodi_asal: dataPendaftaran?.akreditasi_prodi_asal,
      tahun_ijazah_terakhir: dataPendaftaran?.tahun_ijazah_terakhir,
      nilai_IPK: dataPendaftaran?.nilai_IPK,
      is_bekerja: Number(dataPendaftaran?.is_bekerja) === 1 ? true : false,
      tempat_bekerja: dataPendaftaran?.tempat_bekerja,
      penghasilan_bekerja: dataPendaftaran?.penghasilan_bekerja,
    }));
  }, [dataSiswa, dataPendaftaran, setForm, sessionPeriode?.id_sys_periode]);

  useEffect(() => {
    const selectedProvinsi = dataProvinsi?.find(
      (provinsi: Option) => provinsi?.value == form.provinsi,
    );
    if (selectedProvinsi) setIdFeederProvinsi(selectedProvinsi?.id_feeder);

    const selectedKabupaten = dataKabupaten?.find(
      (kabupaten: Option) => kabupaten?.value == form.kabupaten,
    );
    if (selectedKabupaten) setIdFeederKabupaten(selectedKabupaten?.id_feeder);
  }, [
    setIdFeederProvinsi,
    setIdFeederKabupaten,
    dataProvinsi,
    dataKabupaten,
    form,
  ]);

  const isKelasDenpasar =
    dataSiswa?.data_pendaftaran?.[0]?.periode?.sys_prodi?.[0]
      ?.is_kelas_denpasar ?? false;

  // --- RENDER ---
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 border-b border-slate-200 pb-4 dark:border-slate-800">
        <User className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">
            Lengkapi Biodata
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Isi data sesuai dokumen resmi (KTP/Ijazah).
          </p>
        </div>
      </div>

      {isPendingUser || isPendingDataSiswa ? (
        <div className="grid grid-cols-1 gap-6">
          <SkeletonRow className="h-40 w-full rounded-xl" />
          <div className="grid gap-6 md:grid-cols-2">
            {Array(6)
              .fill(null)
              .map((_, index) => (
                <SkeletonRow key={index} className="h-12 w-full" />
              ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {/* --- FOTO PROFIL SECTION --- */}
          <div className="flex flex-col items-center gap-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:flex-row">
            <div
              className="group relative flex h-[160px] w-[120px] shrink-0 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 transition-all hover:border-blue-500 dark:border-slate-700 dark:bg-slate-950"
              onClick={() => uploadRef.current?.click()}
            >
              {preview || form.path_foto ? (
                <img
                  src={preview || form.path_foto || ""}
                  alt="Foto Profil"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full flex-col items-center justify-center text-slate-400">
                  <Camera className="mb-2 h-8 w-8 opacity-50" />
                  <span className="px-2 text-center text-xs font-medium">
                    Upload Foto
                  </span>
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                <FaPlus className="text-white" size={24} />
              </div>
            </div>

            <div className="flex-1 space-y-2 text-center sm:text-left">
              <h3 className="font-semibold text-slate-800 dark:text-slate-200">
                Foto Profil
              </h3>
              <p className="text-sm text-slate-500">
                Unggah pas foto formal terbaru. Wajah harus terlihat jelas
                menghadap ke depan.
                <br className="hidden sm:block" /> Format: JPG/PNG, Maksimal
                1MB.
              </p>
              <div className="text-xs font-medium text-slate-500">
                {file && <p>File: {file.name}</p>}
              </div>
              <InputError
                text={error?.response?.data?.errors?.foto?.join(",")}
              />
            </div>

            <input
              ref={uploadRef}
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) setFile(e.target.files[0]);
              }}
            />
          </div>

          <div className="grid grid-cols-1 gap-6">
            {/* --- INFORMASI UTAMA --- */}
            <FormSection
              icon={<Briefcase className="h-4 w-4 text-blue-600" />}
              title="Informasi Utama"
            >
              <div className="grid grid-cols-1 gap-4">
                {/* Read Only Fields */}
                <div className="grid grid-cols-1 gap-4 rounded-xl border border-slate-100 bg-slate-50 p-3 dark:border-slate-700/50 dark:bg-slate-800/50 md:grid-cols-2">
                  <div className="col-span-1 md:col-span-2">
                    <Label className="text-[10px] uppercase text-slate-500">
                      Program Studi Tujuan (Fast Track)
                    </Label>
                    <div className="font-mono text-xs font-medium">
                      {dataPilihanProdi?.[0]?.unitkerja?.nama || "-"}
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">
                      Email
                    </Label>
                    <div className="font-mono text-xs font-medium">
                      {dataSiswa?.email || "-"}
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">
                      NIK / KTP
                    </Label>
                    <div className="font-mono text-xs font-medium">
                      {dataSiswa?.nik || "-"}
                    </div>
                  </div>
                </div>

                {isKelasDenpasar && (
                  <div>
                    <Label>Lokasi Kuliah</Label>
                    <Select
                      value={form.pilihan_lokasi_kuliah || ""}
                      onValueChange={(value) =>
                        setForm((prev) => ({
                          ...prev,
                          pilihan_lokasi_kuliah: value as LokasiKuliah,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Pilih Lokasi Kuliah" />
                      </SelectTrigger>
                      <SelectContent>
                        {listLokasiKuliah?.map((value, index) => (
                          <SelectItem
                            key={index}
                            value={`${value.toLowerCase()}`}
                          >
                            {value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <InputError
                      text={error?.response?.data?.errors?.pilihan_lokasi_kuliah?.join(
                        ",",
                      )}
                    />
                  </div>
                )}

                <div>
                  <Label required>Nama Lengkap (Tanpa Gelar)</Label>
                  <Input
                    required
                    value={form.nama_lengkap || ""}
                    placeholder="Sesuai Ijazah"
                    onChange={(e) =>
                      setForm({ ...form, nama_lengkap: e.target.value })
                    }
                  />
                  <InputError
                    text={error?.response?.data?.errors?.nama_lengkap?.join(
                      ",",
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Label required>Tempat Lahir</Label>
                    <Input
                      required
                      value={form.tempat_lahir || ""}
                      onChange={(e) =>
                        setForm({ ...form, tempat_lahir: e.target.value })
                      }
                    />
                    <InputError
                      text={error?.response?.data?.errors?.tempat_lahir?.join(
                        ",",
                      )}
                    />
                  </div>
                  <div>
                    <Label required>Tanggal Lahir</Label>
                    <Input
                      required
                      type="date"
                      value={form.tanggal_lahir || ""}
                      onChange={(e) =>
                        setForm({ ...form, tanggal_lahir: e.target.value })
                      }
                    />
                    <InputError
                      text={error?.response?.data?.errors?.tanggal_lahir?.join(
                        ",",
                      )}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Label required>Jenis Kelamin</Label>
                    <Select
                      value={form.jenis_kelamin || ""}
                      onValueChange={(v) =>
                        setForm({ ...form, jenis_kelamin: v as "L" | "P" })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Pilih" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="L">Laki-laki</SelectItem>
                        <SelectItem value="P">Perempuan</SelectItem>
                      </SelectContent>
                    </Select>
                    <InputError
                      text={error?.response?.data?.errors?.jenis_kelamin?.join(
                        ",",
                      )}
                    />
                  </div>
                  <div>
                    <Label required>Agama</Label>
                    <Select
                      value={form.id_ref_agama ? String(form.id_ref_agama) : ""}
                      onValueChange={(v) =>
                        setForm({ ...form, id_ref_agama: v })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Pilih Agama" />
                      </SelectTrigger>
                      <SelectContent>
                        {dataAgama?.map((a: Agama) => (
                          <SelectItem
                            key={a.id_ref_agama}
                            value={String(a.id_ref_agama)}
                          >
                            {a.nama_agama}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <InputError
                      text={error?.response?.data?.errors?.id_ref_agama?.join(
                        ",",
                      )}
                    />
                  </div>
                </div>

                <div>
                  <Label required>No. Handphone (WA)</Label>
                  <Input
                    required
                    maxLength={15}
                    value={form.handphone || ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        handphone: e.target.value.replace(/\D/g, ""),
                      })
                    }
                  />
                  <InputError
                    text={error?.response?.data?.errors?.handphone?.join(",")}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Label>NISN</Label>
                    <Input
                      maxLength={10}
                      value={form.nisn || ""}
                      onChange={(e) =>
                        setForm({ ...form, nisn: e.target.value })
                      }
                    />
                    <InputError
                      text={error?.response?.data?.errors?.nisn?.join(",")}
                    />
                  </div>
                  <div>
                    <Label>NPWP</Label>
                    <Input
                      maxLength={15}
                      value={form.npwp || ""}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          npwp: e.target.value.replace(/\D/g, ""),
                        })
                      }
                    />
                    <InputError
                      text={error?.response?.data?.errors?.npwp?.join(",")}
                    />
                  </div>
                </div>

                <div>
                  <Label required>Kewarganegaraan</Label>
                  <Combobox
                    open={openNegara}
                    setOpen={setOpenNegara}
                    value={form.id_ref_negara || ""}
                    setValue={(v) => setForm({ ...form, id_ref_negara: v })}
                    options={dataNegara}
                    placeholder="Pilih Negara"
                  />
                  <InputError
                    text={error?.response?.data?.errors?.id_ref_negara?.join(
                      ",",
                    )}
                  />
                </div>

                <div>
                  <Label required>Nama Ibu Kandung</Label>
                  <Input
                    required
                    value={form.nama_ibu_kandung || ""}
                    onChange={(e) =>
                      setForm({ ...form, nama_ibu_kandung: e.target.value })
                    }
                  />
                  <InputError
                    text={error?.response?.data?.errors?.nama_ibu_kandung?.join(
                      ",",
                    )}
                  />
                </div>
              </div>
            </FormSection>

            {/* --- DATA FISIK & LAINNYA --- */}
            <FormSection
              icon={<Ruler className="h-4 w-4 text-purple-600" />}
              title="Data Fisik & Lainnya"
            >
              <div className="grid grid-cols-1 gap-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label required>Tinggi(cm)</Label>
                    <Input
                      type="number"
                      value={
                        form.tinggi_badan === 0 ? "" : Number(form.tinggi_badan)
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          tinggi_badan: Number(e.target.value),
                        })
                      }
                    />
                    <InputError
                      text={error?.response?.data?.errors?.tinggi_badan?.join(
                        ",",
                      )}
                    />
                  </div>
                  <div>
                    <Label required>Berat(kg)</Label>
                    <Input
                      type="number"
                      value={
                        form.berat_badan === 0 ? "" : Number(form.berat_badan)
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          berat_badan: Number(e.target.value),
                        })
                      }
                    />
                    <InputError
                      text={error?.response?.data?.errors?.berat_badan?.join(
                        ",",
                      )}
                    />
                  </div>
                  <div>
                    <Label required>Ukuran Jas</Label>
                    <Select
                      value={form.ukuran_baju || ""}
                      onValueChange={(v) =>
                        setForm({ ...form, ukuran_baju: v as Size })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="-" />
                      </SelectTrigger>
                      <SelectContent>
                        {listSize?.map((v, i) => (
                          <SelectItem key={i} value={v}>
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <InputError
                      text={error?.response?.data?.errors?.ukuran_baju?.join(
                        ",",
                      )}
                    />
                  </div>
                </div>

                <div>
                  <Label required>Alat Transportasi</Label>
                  <p className="italic text-muted-foreground text-sm">
                    Isi dengan alat transportasi yang akan digunakan selama
                    kuliah
                  </p>
                  <Combobox
                    open={openAlatTransportasi}
                    setOpen={setOpenAlatTransportasi}
                    value={form.id_ref_alat_transportasi || ""}
                    setValue={(v) =>
                      setForm({ ...form, id_ref_alat_transportasi: v })
                    }
                    options={dataAlatTransportasi}
                    placeholder="Pilih Transportasi"
                  />
                  <InputError
                    text={error?.response?.data?.errors?.id_ref_alat_transportasi?.join(
                      ",",
                    )}
                  />
                </div>
              </div>
            </FormSection>

            {/* --- ALAMAT --- */}
            <FormSection
              icon={<MapPin className="h-4 w-4 text-emerald-600" />}
              title="Alamat Domisili"
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label required>Provinsi</Label>
                  <Combobox
                    serverSide
                    open={openProvinsi}
                    setOpen={setOpenProvinsi}
                    value={form.provinsi || ""}
                    setValue={(v) => {
                      setForm({
                        ...form,
                        provinsi: v,
                        kabupaten: "",
                        kecamatan: "",
                      });
                      const prov = dataProvinsi?.find((p: any) => p.value == v);
                      setIdFeederProvinsi(prov?.id_feeder);
                    }}
                    options={dataProvinsi}
                    placeholder="Pilih Provinsi"
                    onChange={(e) => setSearchQueryProvinsi(e.target.value)}
                    isPending={isPendingProvinsi}
                    searchQueryValue={searchQueryProvinsi}
                  />
                  <InputError
                    text={error?.response?.data?.errors?.provinsi?.join(",")}
                  />
                </div>

                <div>
                  <Label required>Kabupaten</Label>
                  <Combobox
                    serverSide
                    open={openKabupaten}
                    setOpen={setOpenKabupaten}
                    value={form.kabupaten || ""}
                    setValue={(v) => {
                      setForm({ ...form, kabupaten: v, kecamatan: "" });
                      const kab = dataKabupaten?.find((p: any) => p.value == v);
                      setIdFeederKabupaten(kab?.id_feeder);
                    }}
                    options={dataKabupaten}
                    placeholder="Pilih Kabupaten"
                    onChange={(e) => setSearchQueryKabupaten(e.target.value)}
                    isPending={isPendingKabupaten}
                    searchQueryValue={searchQueryKabupaten}
                    disabled={!form.provinsi}
                  />
                  <InputError
                    text={error?.response?.data?.errors?.kabupaten?.join(",")}
                  />
                </div>

                <div>
                  <Label required>Kecamatan</Label>
                  <Combobox
                    serverSide
                    open={openKecamatan}
                    setOpen={setOpenKecamatan}
                    value={form.kecamatan || ""}
                    setValue={(v) => setForm({ ...form, kecamatan: v })}
                    options={dataKecamatan}
                    placeholder="Pilih Kecamatan"
                    onChange={(e) => setSearchQueryKecamatan(e.target.value)}
                    isPending={isPendingKecamatan}
                    searchQueryValue={searchQueryKecamatan}
                    disabled={!form.kabupaten}
                  />
                  <InputError
                    text={error?.response?.data?.errors?.kecamatan?.join(",")}
                  />
                </div>

                <div>
                  <Label required>Desa/Kelurahan</Label>
                  <Input
                    value={form.kelurahan || ""}
                    onChange={(e) =>
                      setForm({ ...form, kelurahan: e.target.value })
                    }
                    placeholder="Nama Desa"
                  />
                  <InputError
                    text={error?.response?.data?.errors?.kelurahan?.join(",")}
                  />
                </div>

                <div className="md:col-span-2">
                  <Label required>Jalan / Alamat Lengkap</Label>
                  <Input
                    value={form.jalan || ""}
                    onChange={(e) =>
                      setForm({ ...form, jalan: e.target.value })
                    }
                    placeholder="Jalan, No. Rumah, Gang"
                  />
                  <InputError
                    text={error?.response?.data?.errors?.jalan?.join(",")}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4 md:col-span-2">
                  <div>
                    <Label required>Kode Pos</Label>
                    <Input
                      maxLength={5}
                      value={form.kode_pos || ""}
                      onChange={(e) =>
                        setForm({ ...form, kode_pos: e.target.value })
                      }
                    />
                    <InputError
                      text={error?.response?.data?.errors?.kode_pos?.join(",")}
                    />
                  </div>
                  <div>
                    <Label>RT</Label>
                    <Input
                      type="number"
                      value={form.rt || ""}
                      onChange={(e) =>
                        setForm({ ...form, rt: Number(e.target.value) })
                      }
                    />
                    <InputError
                      text={error?.response?.data?.errors?.rt?.join(",")}
                    />
                  </div>
                  <div>
                    <Label>RW</Label>
                    <Input
                      type="number"
                      value={form.rw || ""}
                      onChange={(e) =>
                        setForm({ ...form, rw: Number(e.target.value) })
                      }
                    />
                    <InputError
                      text={error?.response?.data?.errors?.rw?.join(",")}
                    />
                  </div>
                </div>

                <div className="pt-2 md:col-span-2">
                  <Label required>Jenis Tinggal</Label>
                  <Select
                    value={
                      form.id_ref_jenis_tinggal
                        ? String(form.id_ref_jenis_tinggal)
                        : ""
                    }
                    onValueChange={(v) =>
                      setForm({ ...form, id_ref_jenis_tinggal: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pilih Jenis Tinggal" />
                    </SelectTrigger>
                    <SelectContent>
                      {dataJenisTinggal?.map((jt: JenisTinggal) => (
                        <SelectItem
                          key={jt.id_ref_jenis_tinggal}
                          value={String(jt.id_ref_jenis_tinggal)}
                        >
                          {jt.nama_jenis_tinggal}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <InputError
                    text={error?.response?.data?.errors?.id_ref_jenis_tinggal?.join(
                      ",",
                    )}
                  />
                </div>

                {selectedJenisTinggal?.nama_jenis_tinggal.startsWith(
                  "Panti",
                ) && (
                  <div className="rounded border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-900/20 md:col-span-2">
                    <Label required>Nama Panti Asuhan</Label>
                    <Input
                      required
                      value={form.nama_panti_asuhan || ""}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          nama_panti_asuhan: e.target.value,
                        })
                      }
                      className="bg-white dark:bg-slate-950"
                    />
                    <InputError
                      text={error?.response?.data?.errors?.nama_panti_asuhan?.join(
                        ",",
                      )}
                    />
                  </div>
                )}
              </div>
            </FormSection>

            {/* --- KONTAK DARURAT (NEW SECTION) --- */}
            <FormSection
              icon={<Contact2 className="h-4 w-4 text-amber-600" />}
              title="Kontak Darurat"
            >
              <div className="grid grid-cols-1 gap-x-6 gap-y-6 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Label required>Hubungan Kontak Darurat</Label>
                  <Select
                    value={form.kontak_darurat_hubungan || ""}
                    onValueChange={(value) => {
                      if (!value) return;
                      setForm((prev) => ({
                        ...prev,
                        kontak_darurat_hubungan: value as HubunganKeluargaEnum,
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pilih hubungan kontak darurat" />
                    </SelectTrigger>
                    <SelectContent>
                      {listHubunganKeluarga?.map(
                        (option: Option, index: number) => (
                          <SelectItem key={index} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <InputError
                    text={error?.response?.data?.errors?.kontak_darurat_hubungan?.join(
                      ",",
                    )}
                  />
                </div>

                <div>
                  <Label required>Nama Kontak Darurat</Label>
                  <Input
                    required
                    placeholder="Nama kontak darurat..."
                    value={form.kontak_darurat_nama || ""}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        kontak_darurat_nama: e.target.value,
                      }))
                    }
                  />
                  <InputError
                    text={error?.response?.data?.errors?.kontak_darurat_nama?.join(
                      ",",
                    )}
                  />
                </div>

                <div>
                  <Label required>No. HP Kontak Darurat</Label>
                  <Input
                    required
                    maxLength={15}
                    value={form.kontak_darurat_hp || ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        kontak_darurat_hp: e.target.value.replace(/\D/g, ""),
                      })
                    }
                    placeholder="08xxxxxxxxxx"
                    inputMode="numeric"
                  />
                  <InputError
                    text={error?.response?.data?.errors?.kontak_darurat_hp?.join(
                      ",",
                    )}
                  />
                </div>
              </div>
            </FormSection>

            {/* --- PENDIDIKAN & PEKERJAAN --- */}
            <FormSection
              icon={<GraduationCap className="h-4 w-4 text-orange-600" />}
              title="Pendidikan Asal & Pekerjaan"
            >
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label required>
                    Universitas / Institusi{" "}
                    {jenjangParams === "S2"
                      ? "S1/D4"
                      : jenjangParams === "S3"
                        ? "S2"
                        : ""}
                  </Label>
                  <Combobox
                    serverSide
                    open={openAsalUniversitas}
                    setOpen={setOpenAsalUniversitas}
                    value={form.asal_pt || ""}
                    setValue={(newValue) =>
                      setForm((prev) => ({ ...prev, asal_pt: newValue }))
                    }
                    onChange={(event) =>
                      setSearchQueryUniversitas(event.target.value)
                    }
                    options={optionsPt}
                    placeholder="Pilih Asal Perguruan Tinggi"
                    isPending={isPendingOptionsPt}
                    searchQueryValue={searchQueryUniversitas || ""}
                  />
                  <InputError
                    text={error?.response?.data?.errors?.asal_pt?.join(",")}
                  />
                </div>

                <div>
                  <Label required>Akreditasi Universitas</Label>
                  <Select
                    value={form.akreditasi_pt_asal || ""}
                    onValueChange={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        akreditasi_pt_asal: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pilih Akreditasi" />
                    </SelectTrigger>
                    <SelectContent>
                      {listAkreditasi?.map((value, index) => (
                        <SelectItem key={index} value={`${value}`}>
                          {value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <InputError
                    text={error?.response?.data?.errors?.akreditasi_pt_asal?.join(
                      ",",
                    )}
                  />
                </div>

                {form.asal_pt && (
                  <>
                    <div>
                      <Label required>Program Studi</Label>
                      <Combobox
                        open={openAsalProdi}
                        setOpen={setOpenAsalProdi}
                        value={form.asal_prodi || ""}
                        setValue={(newValue) =>
                          setForm((prev) => ({
                            ...prev,
                            asal_prodi: newValue,
                          }))
                        }
                        options={optionsAsalProdi}
                        placeholder="Pilih Asal Program Studi"
                        isPending={isPendingOptionsPt}
                      />
                      <InputError
                        text={error?.response?.data?.errors?.asal_prodi?.join(
                          ",",
                        )}
                      />
                    </div>
                    <div>
                      <Label required>Akreditasi Program Studi</Label>
                      <Select
                        value={form.akreditasi_prodi_asal || ""}
                        onValueChange={(value) =>
                          setForm((prev) => ({
                            ...prev,
                            akreditasi_prodi_asal: value,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Pilih Akreditasi" />
                        </SelectTrigger>
                        <SelectContent>
                          {listAkreditasi?.map((value, index) => (
                            <SelectItem key={index} value={`${value}`}>
                              {value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <InputError
                        text={error?.response?.data?.errors?.akreditasi_prodi_asal?.join(
                          ",",
                        )}
                      />
                    </div>
                  </>
                )}

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Label required>Tahun Ijazah Terakhir</Label>
                    <Input
                      minLength={4}
                      maxLength={4}
                      value={form.tahun_ijazah_terakhir || ""}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          tahun_ijazah_terakhir: e.target.value,
                        }))
                      }
                      placeholder="Contoh: 2023"
                    />
                    <InputError
                      text={error?.response?.data?.errors?.tahun_ijazah_terakhir?.join(
                        ",",
                      )}
                    />
                  </div>
                  <div>
                    <Label required>Nilai IPK</Label>
                    <Input
                      type="number"
                      value={form.nilai_IPK || ""}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          nilai_IPK: e.target.value,
                        }))
                      }
                      placeholder="Contoh: 3.50"
                    />
                    <InputError
                      text={error?.response?.data?.errors?.nilai_IPK?.join(",")}
                    />
                  </div>
                </div>

                <div>
                  <Label required>Status Bekerja</Label>
                  <div className="mt-2 flex gap-5">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={form.is_bekerja === false}
                        onCheckedChange={(checked) =>
                          setForm((prev) => ({
                            ...prev,
                            is_bekerja: checked ? false : undefined,
                          }))
                        }
                      />
                      <span className="text-sm">Belum Bekerja</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={form.is_bekerja === true}
                        onCheckedChange={(checked) =>
                          setForm((prev) => ({
                            ...prev,
                            is_bekerja: checked ? true : undefined,
                          }))
                        }
                      />
                      <span className="text-sm">Sudah Bekerja</span>
                    </div>
                  </div>
                  <InputError
                    text={error?.response?.data?.errors?.is_bekerja?.join(",")}
                  />
                </div>

                {form.is_bekerja && (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <Label>Instansi Bekerja</Label>
                      <Input
                        value={form.tempat_bekerja || ""}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            tempat_bekerja: e.target.value,
                          }))
                        }
                        placeholder="Nama instansi..."
                      />
                      <InputError
                        text={error?.response?.data?.errors?.tempat_bekerja?.join(
                          ",",
                        )}
                      />
                    </div>
                    <div>
                      <Label>Penghasilan Kotor</Label>
                      <Input
                        type="number"
                        value={form.penghasilan_bekerja || ""}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            penghasilan_bekerja: e.target.value,
                          }))
                        }
                        placeholder="Rp..."
                      />
                      <InputError
                        text={error?.response?.data?.errors?.penghasilan_bekerja?.join(
                          ",",
                        )}
                      />
                    </div>
                  </div>
                )}
              </div>
            </FormSection>

            {/* --- KEBUTUHAN KHUSUS --- */}
            <FormSection
              icon={<AlertCircle className="h-4 w-4 text-red-500" />}
              title="Kebutuhan Khusus"
            >
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {listKebutuhanKhusus.map((value, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Checkbox
                      checked={form?.kebutuhan_khusus?.includes(value) || false}
                      onCheckedChange={(isChecked) => {
                        setForm((prev) => {
                          const updated = isChecked
                            ? [...(prev.kebutuhan_khusus || []), value]
                            : (prev.kebutuhan_khusus || []).filter(
                                (item) => item !== value,
                              );
                          return { ...prev, kebutuhan_khusus: updated };
                        });
                      }}
                    />
                    <Label className="text-xs font-normal">{value}</Label>
                  </div>
                ))}
              </div>
            </FormSection>
          </div>
        </div>
      )}
    </div>
  );
}
